import * as PIXI from 'pixi.js';
import { Game } from './core/Game';
import { StartScreen } from './ui/StartScreen';
import { MainMenu } from './ui/MainMenu';
import { ScreenTransition } from './ui/ScreenTransition';
import { MapEditor } from './editor/MapEditor';
import { LoadMapScene } from './editor/LoadMapScene';
import { MapPlayScene } from './play/MapPlayScene';
import { TowerInfoPage } from './ui/TowerInfoPage';
import { GameInfoPage } from './ui/GameInfoPage';
import { AboutPage } from './ui/AboutPage';
import { CreditsPage } from './ui/CreditsPage';
import { SettingsMenu } from './ui/SettingsMenu';
import { MapStorage } from './editor/MapStorage';
import type { MapDefinition } from './editor/MapTypes';

const container = document.getElementById('game-container');
if (!container) {
  throw new Error('Game container not found');
}

const game = new Game(container);
game.setCanvasVisible(false);

// Initialize default maps on startup
MapStorage.initializeDefaults();

const audioSystem = game.getAudioSystem() ?? undefined;
const transition = new ScreenTransition();

let mainMenu: MainMenu;
let loadMapScene: LoadMapScene;
let playScene: MapPlayScene | null = null;
let towerInfoPage: TowerInfoPage;
let gameInfoPage: GameInfoPage;
let aboutPage: AboutPage;
let creditsPage: CreditsPage;

const mapEditor = new MapEditor({
  onClose: () => {
    mainMenu?.show();
  },
  onTestMap: (map: MapDefinition) => {
    mapEditor.hide();
    playScene = new MapPlayScene(map, {
      onBack: () => {
        if (playScene) {
          playScene.hide();
          playScene = null;
        }
        mapEditor.show();
      },
    });
    playScene.show();
  },
});

loadMapScene = new LoadMapScene({
  onBack: () => {
    loadMapScene.hide();
    mainMenu.show();
  },
  onEditor: () => {
    loadMapScene.hide();
    mapEditor.show();
  },
});

// Create a separate container for info pages that's always visible
const infoPagesContainer = document.createElement('div');
infoPagesContainer.style.position = 'fixed';
infoPagesContainer.style.inset = '0';
infoPagesContainer.style.pointerEvents = 'none';
infoPagesContainer.style.zIndex = '9997'; // Lower than MainMenu (9998) but higher than game
infoPagesContainer.style.backgroundColor = 'transparent';
document.body.appendChild(infoPagesContainer);

// Create a PIXI app for info pages (always visible, separate from game)
const infoPagesApp = new PIXI.Application({
  width: 1920,
  height: 1080,
  backgroundColor: 0x000000,
  backgroundAlpha: 0, // Transparent background - pages will draw their own backgrounds
  antialias: false,
  resolution: 1,
});

// Use canvas or view depending on Pixi.js version (same pattern as Game.ts)
// @ts-ignore - PIXI version compatibility
let infoPagesCanvas: HTMLCanvasElement | null = (infoPagesApp.canvas || infoPagesApp.view) as HTMLCanvasElement;
if (infoPagesCanvas) {
  infoPagesContainer.appendChild(infoPagesCanvas);
  infoPagesCanvas.style.pointerEvents = 'none'; // Start with pointer events disabled
  infoPagesCanvas.style.backgroundColor = 'transparent';
} else {
  console.error('Info pages canvas not found!');
  infoPagesCanvas = null;
}

// Store canvas reference globally for info pages to access
(window as any).infoPagesCanvas = infoPagesCanvas;

// Create info pages
towerInfoPage = new TowerInfoPage({
  onClose: () => {
    towerInfoPage.hide();
    mainMenu.show();
  },
  audioSystem,
});
infoPagesApp.stage.addChild(towerInfoPage.getContainer());

gameInfoPage = new GameInfoPage({
  onClose: () => {
    gameInfoPage.hide();
    mainMenu.show();
  },
  audioSystem,
});
infoPagesApp.stage.addChild(gameInfoPage.getContainer());

aboutPage = new AboutPage({
  onClose: () => {
    aboutPage.hide();
    mainMenu.show();
  },
  audioSystem,
});
infoPagesApp.stage.addChild(aboutPage.getContainer());

creditsPage = new CreditsPage({
  onClose: () => {
    creditsPage.hide();
    mainMenu.show();
  },
  audioSystem,
});
infoPagesApp.stage.addChild(creditsPage.getContainer());

// Create standalone settings menu for main menu
const standaloneSettingsMenu = new SettingsMenu();
standaloneSettingsMenu.onBackToMainMenu(() => {
  standaloneSettingsMenu.hide();
  mainMenu.show();
});
infoPagesApp.stage.addChild(standaloneSettingsMenu.getContainer());

// Store mainMenu instance globally for language updates (will be set after mainMenu is created)

// Create main menu and store instance globally
mainMenu = new MainMenu({
  onSinglePlayer: () => {
    game.setCanvasVisible(true);
    game.startSinglePlayerSession();
  },
  onBackToStartScreen: () => {
    showStartScreen();
  },
  onOpenMapEditor: () => {
    mainMenu.hide();
    mapEditor.show();
  },
  onOpenLoadMap: () => {
    mainMenu.hide();
    loadMapScene.show();
  },
  onOpenSettings: () => {
    mainMenu.hide();
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
    standaloneSettingsMenu.show();
  },
  onOpenTowerInfo: () => {
    mainMenu.hide();
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
    towerInfoPage.show();
  },
  onOpenGameInfo: () => {
    mainMenu.hide();
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
    gameInfoPage.show();
  },
  onOpenAbout: () => {
    mainMenu.hide();
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
    aboutPage.show();
  },
  onOpenCredits: () => {
    mainMenu.hide();
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
    creditsPage.show();
  },
}, audioSystem, transition);

// Store reference for language updates
(window as any).mainMenuInstance = mainMenu;

function showStartScreen(): void {
  game.setCanvasVisible(false);
  mainMenu.hide();
  mapEditor.hide();
  new StartScreen(
    async () => {
      mainMenu.show();
    },
    async () => {
      await game.ensureAudioReady();
      audioSystem?.playSound('button_click');
    },
  );
}

showStartScreen();

game.onMainMenuRequested(() => {
  transition.fadeOutOnly(() => {
    window.location.reload();
  });
});

console.log('Mazing Hunters v1 - Main Menu ready');

